import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getChannelShare,
  inviteCollaborator,
  removeCollaborator,
} from "@/lib/api";

/**
 * Collaborators card for an Active Channel detail page.
 *
 * Equal-owner model:
 *   • Anyone on the team can invite up to 4 additional collaborators
 *     (5 total members incl. the owner).
 *   • Anyone on the team can remove any NON-owner collaborator.
 *   • The owner can NEVER be removed via this UI — they alone can
 *     delete the entire channel from the channel actions area.
 */
export default function CollaboratorsCard({ channelId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await getChannelShare(channelId);
      setData(r);
    } catch (err) {
      console.debug("Couldn't load collaborators:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const handleInvite = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed) return;
    setInviting(true);
    const tid = toast.loading(`Inviting ${trimmed}…`);
    try {
      await inviteCollaborator(channelId, trimmed);
      toast.success(`${trimmed} added to the team`, { id: tid });
      setEmail("");
      await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Invite failed", { id: tid },
      );
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberEmail) => {
    if (!window.confirm(
      `Remove ${memberEmail} from this channel? They'll lose access immediately.`,
    )) return;
    const tid = toast.loading(`Removing ${memberEmail}…`);
    try {
      await removeCollaborator(channelId, memberEmail);
      toast.success(`${memberEmail} removed`, { id: tid });
      await refresh();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Remove failed", { id: tid },
      );
    }
  };

  if (loading) {
    return (
      <section
        data-testid="collaborators-card"
        className="border border-zinc-800 rounded-sm p-4 bg-black/40"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          // COLLABORATORS · loading…
        </div>
      </section>
    );
  }

  const members = data?.members || [];
  const slotsRemaining = data?.slots_remaining ?? 0;
  const maxCollaborators = data?.max_collaborators ?? 4;
  const isOwner = !!data?.is_owner;

  return (
    <section
      data-testid="collaborators-card"
      className="border border-zinc-800 rounded-sm p-4 bg-black/40 space-y-4"
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-bold">
            // COLLABORATORS
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
            {members.length} member{members.length === 1 ? "" : "s"}
            {" · "}
            <span data-testid="collab-slots-remaining">
              {slotsRemaining}
            </span>
            /{maxCollaborators} invite slot{maxCollaborators === 1 ? "" : "s"} left
          </div>
        </div>
      </div>

      <ul
        data-testid="collab-member-list"
        className="space-y-1.5"
      >
        {members.map((m) => (
          <li
            key={m.user_id || m.email}
            data-testid={`collab-row-${m.email}`}
            className={`flex items-center justify-between gap-3 px-3 py-2 rounded-sm border ${
              m.role === "owner"
                ? "border-emerald-500/40 bg-emerald-500/[0.04]"
                : "border-zinc-800 bg-black"
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-display font-black text-[11px] ${
                  m.role === "owner"
                    ? "bg-emerald-400 text-black"
                    : "bg-cyan-400 text-black"
                }`}
                aria-hidden="true"
              >
                {(m.name || m.email || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 truncate">
                  {m.name || m.email}
                  {m.role === "owner" && (
                    <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.25em] text-emerald-300">
                      · owner
                    </span>
                  )}
                </div>
                {m.name && (
                  <div className="font-mono text-[10px] text-zinc-500 truncate">
                    {m.email}
                  </div>
                )}
              </div>
            </div>
            {m.role !== "owner" && (
              <button
                type="button"
                data-testid={`collab-remove-${m.email}`}
                onClick={() => handleRemove(m.email)}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-300 border border-zinc-800 hover:border-red-500/50 px-2.5 py-1 rounded-sm shrink-0"
                title="Remove from channel"
              >
                × Remove
              </button>
            )}
          </li>
        ))}
      </ul>

      {slotsRemaining > 0 ? (
        <form
          onSubmit={handleInvite}
          data-testid="collab-invite-form"
          className="flex flex-wrap items-center gap-2 pt-2 border-t border-zinc-800"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            disabled={inviting}
            data-testid="collab-invite-email"
            className="flex-1 min-w-[200px] bg-black border border-zinc-800 focus:border-cyan-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100"
          />
          <button
            type="submit"
            disabled={inviting || !email.trim()}
            data-testid="collab-invite-btn"
            className={`font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded-sm transition-colors ${
              !inviting && email.trim()
                ? "bg-cyan-400 text-black hover:bg-cyan-300 font-bold"
                : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
            }`}
          >
            {inviting ? "▸ inviting…" : "+ Invite teammate"}
          </button>
        </form>
      ) : (
        <div
          data-testid="collab-cap-reached"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300 border border-amber-400/30 rounded-sm bg-amber-400/[0.04] px-3 py-2"
        >
          ▸ team is full ({maxCollaborators} collaborators) — remove someone first to add another
        </div>
      )}

      {!isOwner && (
        <div
          className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500"
          data-testid="collab-shared-note"
        >
          ▸ you joined this channel as a collaborator · equal-owner access to idea bank, animate, stitch
        </div>
      )}
    </section>
  );
}
